'use client';

import { components } from '@package/types/integrations/content/umbraco';
import React, { useEffect } from 'react';
import Icon from '@/components/atoms/icon/icon';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import ProfileTripInvitation from '@/components/blocks/profile-trip-invitation/profile-trip-invitation';
import Button from '@/components/molecules/button/button';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Link from '@/components/utilities/link/link';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import {
  trackPageLoadEvent,
  trackViewEvent,
} from '@/integrations/tracking/google-tag-manager/scripts';
import { tealiumView } from '@/integrations/tracking/tealium/scripts';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useIsIOS from '@/libraries/hooks/use-is-ios';
import styles from './co-travelers-landing.module.scss';

export interface CoTravelersLandingProps extends ModuleWrapperProps {
  /** The type of the page */
  $type: 'CoTravelersLandingPageDto';
  /** The button to download the app for android users */
  androidButton?: components['schemas']['LinkDto'] | null;
  /** The headline to show */
  headline?: string;
  /** The button to download the app for ios users */
  iosButton?: components['schemas']['LinkDto'] | null;
  /** The list of benefits to show */
  items?: components['schemas']['CoTravelersLandingPageItemDto'][] | null;
  /** The items label to show */
  itemsLabel?: string | null;
  /** The link to send users to the profile page to accept the invitation */
  link?: components['schemas']['LinkDto'] | null;
  /** Personalization data for the user */
  personalizationData?: Content.Properties.PersonalizationData;
  /** The pre headline to show */
  preHeadline?: string;
  /** The text to show */
  text?: string;
  /** Tracking data for the page */
  trackingData?: Content.Properties.TrackingData;
  /** The trip */
  trip?: components['schemas']['InvitationTripDto'] | null;
}

const CoTravelersLanding = ({
  androidButton,
  headline,
  iosButton,
  items,
  itemsLabel,
  link,
  personalizationData,
  preHeadline,
  text,
  trackingData,
  trip,
  ...rest
}: CoTravelersLandingProps) => {
  const { t } = useI18n();

  const isIOS = useIsIOS();

  const downloadButton = isIOS ? iosButton : androidButton;

  useEffect(() => {
    trackPageLoadEvent({ content_group: 'trip-invitation-landing' });

    personalizationData && tealiumView(personalizationData);

    if (!trackingData?.gtmDataLayer) return;

    trackViewEvent(trackingData.gtmDataLayer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ModuleWrapper {...rest} className={styles.component}>
      <Container className={styles.container}>
        {(preHeadline || headline || text) && (
          <Row>
            <Column
              offset={{ laptop: 2, tablet: 1 }}
              width={{ laptop: 8, tablet: 10 }}
            >
              <div className={styles.header}>
                {preHeadline && (
                  <Typography
                    className={styles.preHeadline}
                    tagStyle="labelLarge"
                  >
                    {preHeadline}
                  </Typography>
                )}

                {headline && (
                  <Typography
                    className={styles.headline}
                    tagStyle="headingMedium"
                  >
                    {headline}
                  </Typography>
                )}

                {text && <RichText className={styles.text} text={text} />}
              </div>
            </Column>
          </Row>
        )}

        {trip && (
          <React.Fragment>
            <Row>
              <Column
                offset={{ laptop: 2, tablet: 1 }}
                width={{ laptop: 8, tablet: 10 }}
              >
                <div className={styles.invitationWrapper}>
                  <ProfileTripInvitation hideAction trip={trip} />

                  {downloadButton && (
                    <Link
                      className={styles.downloadAppLink}
                      url={downloadButton.url}
                    >
                      <Icon
                        name={isIOS ? 'AccountApple' : 'GooglePlay'}
                        size={24}
                      />

                      <div className={styles.downloadAppLinkText}>
                        <Typography tagStyle="labelSmall">
                          {t(
                            'items.profile.profilePage.coTravelers.downloadOn',
                          )}
                        </Typography>

                        <Typography tagStyle="cta">
                          {downloadButton.text}
                        </Typography>
                      </div>
                    </Link>
                  )}
                </div>
              </Column>
            </Row>

            <Row>
              <Column
                offset={{ laptop: 4, tablet: 2 }}
                width={{ laptop: 4, tablet: 8 }}
              >
                <div className={styles.items}>
                  {items && items.length > 0 && (
                    <div className={styles.benefits}>
                      {itemsLabel && (
                        <Typography
                          className={styles.benefitsHeadline}
                          tagStyle="headingSmall"
                        >
                          {itemsLabel}
                        </Typography>
                      )}

                      <div className={styles.benefitItems}>
                        {items.map((item, itemIndex) => {
                          return (
                            <div key={itemIndex} className={styles.benefitItem}>
                              <div className={styles.iconWrapper}>
                                <Icon
                                  className={styles.icon}
                                  name={item.icon}
                                  size={16}
                                />
                              </div>

                              <div className={styles.benefitDetails}>
                                <Typography
                                  className={styles.benefitTitle}
                                  tagStyle="bodyMediumBold"
                                >
                                  {item.headline}
                                </Typography>

                                <Typography
                                  className={styles.benefitsDescription}
                                  tagStyle="bodySmall"
                                >
                                  {item.description}
                                </Typography>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className={styles.separator}>
                    <div className={styles.line} />

                    <Typography className={styles.or} tagStyle="bodyMediumBold">
                      {t('items.profile.profilePage.coTravelers.or')}
                    </Typography>

                    <div className={styles.line} />
                  </div>
                </div>
              </Column>
            </Row>
          </React.Fragment>
        )}

        {link && (
          <Row>
            <Column
              offset={{ laptop: 4, tablet: 2 }}
              width={{ laptop: 4, tablet: 8 }}
            >
              <div className={styles.homepageLinkWrapper}>
                <Button
                  className={styles.homePageLink}
                  icon="Globe"
                  iconPosition="left"
                  iconSize={16}
                  text={link.text}
                  url={link.url}
                  variant="secondary"
                />
              </div>
            </Column>
          </Row>
        )}
      </Container>
    </ModuleWrapper>
  );
};

CoTravelersLanding.displayName = 'CoTravelersLanding';

export default CoTravelersLanding;
